import { useQuery } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { useState } from "react";
import { X, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Promotion {
  id: number;
  title: string;
  body: string;
  audience: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
}

function storageKey(userId: string | null | undefined): string {
  return `rheumhub_dismissed_promotions_${userId ?? "anon"}`;
}

function getDismissed(userId: string | null | undefined): number[] {
  try {
    return JSON.parse(localStorage.getItem(storageKey(userId)) ?? "[]") as number[];
  } catch {
    return [];
  }
}

function addDismissed(userId: string | null | undefined, id: number) {
  const key = storageKey(userId);
  const current = getDismissed(userId);
  if (!current.includes(id)) {
    localStorage.setItem(key, JSON.stringify([...current, id]));
  }
}

export function PromotionBanners({ audience }: { audience: "nurse" | "patient" }) {
  const { user } = useUser();
  const userId = user?.id ?? null;

  const [dismissed, setDismissed] = useState<number[]>(() => getDismissed(userId));

  const { data: promotions = [] } = useQuery<Promotion[]>({
    queryKey: ["promotions", audience],
    queryFn: async () => {
      const res = await fetch(`${basePath}/api/promotions?audience=${audience}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<Promotion[]>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const visible = promotions.filter((p) => !dismissed.includes(p.id));

  function dismiss(id: number) {
    addDismissed(userId, id);
    setDismissed((prev) => [...prev, id]);
  }

  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {visible.map((p) => (
        <div
          key={p.id}
          className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 flex items-start gap-3"
        >
          <Megaphone className="h-4 w-4 text-teal-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-teal-900 text-sm">{p.title}</p>
            <p className="text-sm text-teal-800 mt-0.5">{p.body}</p>
            {p.ctaLabel && p.ctaUrl && (
              <a
                href={p.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs font-medium text-teal-700 underline hover:text-teal-900"
              >
                {p.ctaLabel} →
              </a>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-teal-600 hover:text-teal-900 hover:bg-teal-100 flex-shrink-0"
            onClick={() => dismiss(p.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
